import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import type { JobListRow, JobSortField, JobStatus } from "@/types";
import { JOB_STATUSES, JOB_ARCHIVED_STATUSES, JOB_ON_HOLD_STATUSES } from "@/types";
import { Button, Spinner, SelectField } from "@/components/ui";
import { jobStatusLabel } from "@/lib/jobs";
import { formatDateShort } from "@/lib/format";
import { formatMoney } from "@/lib/money";
import { WorkstreamStatusCell, JobStagePill } from "./JobChips";
import { NewJobDrawer } from "./NewJobDrawer";
import { useJobsList } from "./useJobsList";
import "./jobs.css";

const PER_PAGE = 50;

/** The sort_field values offered in the toolbar select. The default is
 *  last_activity desc (most-recently-active first, so reactivated jobs surface);
 *  the column headers drive the same state. (The server also supports
 *  days_remaining / next_deadline, but those columns + options were retired from
 *  this triage view.) */
const SORT_OPTIONS: { value: JobSortField; label: string }[] = [
  { value: "last_activity", label: "Last active" },
  { value: "job_number", label: "Job number" },
  { value: "job_name", label: "Job name" },
  { value: "client_display", label: "Client" },
  { value: "stage", label: "Stage" },
  { value: "open_work_count", label: "Open work" },
  { value: "total_invoiced", label: "Invoiced to date" },
];

/** The invoiced-amount pill's colour + tooltip. Payment state takes precedence to
 *  keep the cell uncluttered: an overdue balance turns the pill red (`overdue`) and
 *  an outstanding/unpaid balance turns it amber (`unpaid`), regardless of invoice
 *  completeness. With nothing outstanding the colour falls back to completeness —
 *  `full` (green) when the contracted value is fully invoiced, `part` (stone) when
 *  fees remain to invoice, and `none` (muted) for invoiced-but-no-fee jobs (e.g.
 *  Xero-imported invoices) where there's no contracted value to compare. */
function billingPill(row: JobListRow): {
  tone: "full" | "part" | "none" | "unpaid" | "overdue";
  title: string;
} {
  const inv = formatMoney(row.total_invoiced);
  // Payment state first — an outstanding/overdue balance is the headline, so it
  // colours the pill (amber/red) ahead of any completeness signal.
  if (row.has_overdue_invoice) {
    return { tone: "overdue", title: `${inv} invoiced · ${formatMoney(row.total_outstanding)} outstanding — past due` };
  }
  if (row.has_unpaid_invoice) {
    return { tone: "unpaid", title: `${inv} invoiced · ${formatMoney(row.total_outstanding)} outstanding` };
  }
  // Nothing outstanding → colour by invoice completeness.
  if (row.total_agreed <= 0) {
    return { tone: "none", title: `${inv} invoiced — no fees recorded on this job` };
  }
  const agreed = formatMoney(row.total_agreed);
  if (row.is_fully_invoiced) {
    return { tone: "full", title: `Fully invoiced — ${inv} of ${agreed}` };
  }
  return { tone: "part", title: `${inv} of ${agreed} invoiced — ${formatMoney(row.remaining_to_invoice)} left` };
}

const DEFAULT_SORT_FIELD: JobSortField = "last_activity";
const DEFAULT_SORT_DIRECTION: "asc" | "desc" = "desc";

/** The natural starting direction when a field is freshly selected (vs. a repeat
 *  click, which flips). Recency reads best newest-first; everything else ascends
 *  (A–Z, soonest deadline, lowest count). */
function naturalDirection(field: JobSortField): "asc" | "desc" {
  return field === "last_activity" ? "desc" : "asc";
}

/** The toolbar/column sort persists across visits (localStorage), so returning
 *  to the list restores the field + direction you last used. Stored values are
 *  validated against SORT_OPTIONS so a removed/renamed sort can't wedge the
 *  list, and storage failures (private mode) fall back to the defaults. The `v2`
 *  suffix retires the old job_number default so everyone adopts last_activity. */
const SORT_PREF_KEY = "arkitectly.jobsList.sort.v2";

function loadSortPref(): { field: JobSortField; direction: "asc" | "desc" } {
  try {
    const raw = localStorage.getItem(SORT_PREF_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as { field?: unknown; direction?: unknown };
      const field = SORT_OPTIONS.find((o) => o.value === parsed.field)?.value;
      const direction = parsed.direction === "asc" || parsed.direction === "desc" ? parsed.direction : undefined;
      if (field && direction) return { field, direction };
    }
  } catch {
    /* malformed JSON or storage unavailable — fall through to defaults */
  }
  return { field: DEFAULT_SORT_FIELD, direction: DEFAULT_SORT_DIRECTION };
}

function saveSortPref(field: JobSortField, direction: "asc" | "desc"): void {
  try {
    localStorage.setItem(SORT_PREF_KEY, JSON.stringify({ field, direction }));
  } catch {
    /* storage unavailable — preference stays in memory for this visit only */
  }
}

/** The active search + filter set ("what subset am I working on") persists for
 *  the SESSION only (sessionStorage), so clicking into a job and coming back
 *  keeps your search/filters — but a fresh app open starts clean. This is task
 *  context, deliberately separate from the sort preference above, which is a
 *  long-lived view choice and so persists across sessions (localStorage). Stored
 *  stage values are validated against JOB_STATUSES so a removed status can't
 *  wedge the list, and storage failures (private mode) fall back to no filters. */
const FILTER_PREF_KEY = "arkitectly.jobsList.filters.v1";

interface StoredFilters {
  q: string;
  stages: JobStatus[];
  includeArchived: boolean;
  includeOnHold: boolean;
  hasOverdue: boolean;
  hasOpenTasks: boolean;
  hasUnpaid: boolean;
  notFullyInvoiced: boolean;
}

// Default view hides archived AND on-hold jobs: `includeArchived: false` /
// `includeOnHold: false` make the seam send `exclude_archived: true` /
// `exclude_on_hold: true`, so both are excluded until the user opts in. On-hold
// jobs live on the Jobs › On Hold page; completed jobs stay visible here.
const EMPTY_FILTERS: StoredFilters = {
  q: "",
  stages: [],
  includeArchived: false,
  includeOnHold: false,
  hasOverdue: false,
  hasOpenTasks: false,
  hasUnpaid: false,
  notFullyInvoiced: false,
};

function loadFilterPref(): StoredFilters {
  try {
    const raw = sessionStorage.getItem(FILTER_PREF_KEY);
    if (raw) {
      const p = JSON.parse(raw) as Partial<Record<keyof StoredFilters, unknown>>;
      const includeArchived = Boolean(p.includeArchived);
      const includeOnHold = Boolean(p.includeOnHold);
      // A hidden-status chip can't be shown, so a restored archived/on-hold stage
      // selection (e.g. an older pref) is dropped when its toggle is off — it would
      // otherwise AND with the exclude filter into an empty list with no chip to undo.
      const stages = Array.isArray(p.stages)
        ? p.stages.filter(
            (s): s is JobStatus =>
              (JOB_STATUSES as readonly string[]).includes(s as string) &&
              (includeArchived || !JOB_ARCHIVED_STATUSES.includes(s as JobStatus)) &&
              (includeOnHold || !JOB_ON_HOLD_STATUSES.includes(s as JobStatus)),
          )
        : [];
      return {
        q: typeof p.q === "string" ? p.q : "",
        stages,
        includeArchived,
        includeOnHold,
        hasOverdue: Boolean(p.hasOverdue),
        hasOpenTasks: Boolean(p.hasOpenTasks),
        hasUnpaid: Boolean(p.hasUnpaid),
        notFullyInvoiced: Boolean(p.notFullyInvoiced),
      };
    }
  } catch {
    /* malformed JSON or storage unavailable — fall through to no filters */
  }
  return EMPTY_FILTERS;
}

function saveFilterPref(f: StoredFilters): void {
  try {
    sessionStorage.setItem(FILTER_PREF_KEY, JSON.stringify(f));
  } catch {
    /* storage unavailable — filters stay in memory for this visit only */
  }
}

export function JobsList() {
  // Restore this session's search + filter set so job → back → list keeps the
  // subset you were working on. Parsed once (lazy init); see loadFilterPref.
  const [restored] = useState(loadFilterPref);
  const [qInput, setQInput] = useState(restored.q);
  const [q, setQ] = useState(restored.q);
  const [stages, setStages] = useState<Set<JobStatus>>(() => new Set(restored.stages));
  const [includeArchived, setIncludeArchived] = useState(restored.includeArchived);
  const [includeOnHold, setIncludeOnHold] = useState(restored.includeOnHold);
  const [hasOverdue, setHasOverdue] = useState(restored.hasOverdue);
  const [hasOpenTasks, setHasOpenTasks] = useState(restored.hasOpenTasks);
  const [hasUnpaid, setHasUnpaid] = useState(restored.hasUnpaid);
  const [notFullyInvoiced, setNotFullyInvoiced] = useState(restored.notFullyInvoiced);
  const [sortField, setSortField] = useState<JobSortField>(() => loadSortPref().field);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">(() => loadSortPref().direction);
  const [page, setPage] = useState(1);
  const [showNew, setShowNew] = useState(false);

  const stagesParam = useMemo(() => [...stages].join(","), [stages]);

  // The archived/on-hold chips only show when their "Include …" toggle is on —
  // otherwise picking one would AND with the default exclude filter into an empty
  // list. Turning a toggle off also drops that stage if it was already selected.
  // (Completed stays selectable: completed jobs are shown by default now.)
  const visibleStatuses = useMemo(
    () =>
      JOB_STATUSES.filter(
        (s) =>
          (includeArchived || !JOB_ARCHIVED_STATUSES.includes(s)) &&
          (includeOnHold || !JOB_ON_HOLD_STATUSES.includes(s)),
      ),
    [includeArchived, includeOnHold],
  );

  function toggleIncludeArchived(next: boolean) {
    setIncludeArchived(next);
    if (!next) {
      setStages((prev) => {
        const cleaned = new Set([...prev].filter((s) => !JOB_ARCHIVED_STATUSES.includes(s)));
        return cleaned.size === prev.size ? prev : cleaned;
      });
    }
    setPage(1);
  }

  function toggleIncludeOnHold(next: boolean) {
    setIncludeOnHold(next);
    if (!next) {
      setStages((prev) => {
        const cleaned = new Set([...prev].filter((s) => !JOB_ON_HOLD_STATUSES.includes(s)));
        return cleaned.size === prev.size ? prev : cleaned;
      });
    }
    setPage(1);
  }

  const location = useLocation();
  const navigate = useNavigate();

  // The header's "New job" routes here with { create: true } — open the drawer
  // and strip the flag so a refresh/back doesn't re-open it.
  useEffect(() => {
    if ((location.state as { create?: boolean } | null)?.create) {
      setShowNew(true);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location, navigate]);

  // Debounce the search box. Resetting page here (and at every other filter
  // mutation) keeps the fetch effect single-firing — page + filter change land
  // in one batched render rather than triggering a wasted request.
  useEffect(() => {
    const t = setTimeout(() => {
      setQ(qInput.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [qInput]);

  // Remember the sort field + direction across visits (toolbar select and the
  // column headers both feed this state, so either path is persisted).
  useEffect(() => {
    saveSortPref(sortField, sortDirection);
  }, [sortField, sortDirection]);

  // Persist the search + filter set for this session so returning to the list
  // (after opening a job) restores the same subset. We store `q` (the debounced,
  // applied value, not the raw keystroke) so the search box and the query
  // restore in lockstep.
  useEffect(() => {
    saveFilterPref({ q, stages: [...stages], includeArchived, includeOnHold, hasOverdue, hasOpenTasks, hasUnpaid, notFullyInvoiced });
  }, [q, stages, includeArchived, includeOnHold, hasOverdue, hasOpenTasks, hasUnpaid, notFullyInvoiced]);

  // The list fetch lives in useJobsList: a module-level SWR cache + in-flight
  // dedupe. The cache makes job → back → list instant (cached paint, background
  // revalidate); the dedupe collapses any concurrent identical call — including
  // StrictMode's dev double-invoke — to a single request.
  const filters = useMemo(
    () => ({
      page,
      per_page: PER_PAGE,
      sort_field: sortField,
      sort_direction: sortDirection,
      q: q || undefined,
      stages: stagesParam || undefined,
      // Exclude-archived / exclude-on-hold are the defaults; the seam drops each
      // flag when false, so ticking "Include archived" / "Include on hold" widens
      // the query back. Completed jobs are always included regardless of these.
      exclude_archived: !includeArchived,
      exclude_on_hold: !includeOnHold,
      has_overdue: hasOverdue,
      has_open_tasks: hasOpenTasks,
      has_unpaid: hasUnpaid,
      not_fully_invoiced: notFullyInvoiced,
    }),
    [page, q, stagesParam, includeArchived, includeOnHold, hasOverdue, hasOpenTasks, hasUnpaid, notFullyInvoiced, sortField, sortDirection],
  );
  const { items, total, totalPages, loading, refreshing, error } = useJobsList(filters);

  // The error string is owned by the fetch hook; track the one the user has
  // clicked-to-dismiss so a fresh/different error still surfaces.
  const [dismissedError, setDismissedError] = useState<string | undefined>();
  const visibleError = error && error !== dismissedError ? error : undefined;

  function applySort(field: JobSortField) {
    if (field === sortField) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection(naturalDirection(field));
    }
    setPage(1);
  }

  function toggleStage(s: JobStatus) {
    setStages((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });
    setPage(1);
  }

  const hasActiveFilters =
    q !== "" || stages.size > 0 || includeArchived || includeOnHold || hasOverdue || hasOpenTasks || hasUnpaid || notFullyInvoiced;

  // Reset the whole working set in one click (search + stage chips + toggles).
  // Sort is intentionally left as-is — it's a separate, long-lived view choice,
  // not part of "the subset I'm working on", and clearing the persisted filters
  // here lets the next save write an empty set.
  function clearAllFilters() {
    setQInput("");
    setQ("");
    setStages(new Set());
    setIncludeArchived(false);
    setIncludeOnHold(false);
    setHasOverdue(false);
    setHasOpenTasks(false);
    setHasUnpaid(false);
    setNotFullyInvoiced(false);
    setPage(1);
  }

  return (
    <section className="jobs-list">
      <header className="jobs-list__head">
        <div>
          <h1>Jobs</h1>
          <p className="jobs-list__count">
            {total} job{total === 1 ? "" : "s"}
            {refreshing ? <span className="jobs-list__refreshing"> · Refreshing…</span> : null}
          </p>
        </div>
      </header>

      <div className="jobs-list__filters">
        <input
          className="leads__search jobs-list__search"
          type="search"
          placeholder="Search job #, client, address, postcode…"
          value={qInput}
          onChange={(e) => setQInput(e.target.value)}
        />
        <SelectField
          label="Sort by"
          className="jobs-list__select"
          value={sortField}
          onChange={(e) => {
            const field = e.target.value as JobSortField;
            setSortField(field);
            setSortDirection(naturalDirection(field));
            setPage(1);
          }}
        >
          {SORT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </SelectField>
        <Button
          variant="ghost"
          size="sm"
          className="jobs-list__dir"
          onClick={() => {
            setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
            setPage(1);
          }}
          title="Toggle sort direction"
        >
          {sortDirection === "asc" ? "↑ Ascending" : "↓ Descending"}
        </Button>
      </div>

      <div className="jobs-list__toggles">
        <label className="jobs-list__toggle">
          <input type="checkbox" checked={includeArchived} onChange={(e) => toggleIncludeArchived(e.target.checked)} />
          Include archived
        </label>
        <label className="jobs-list__toggle">
          <input type="checkbox" checked={includeOnHold} onChange={(e) => toggleIncludeOnHold(e.target.checked)} />
          Include on hold
        </label>
        <label className="jobs-list__toggle">
          <input type="checkbox" checked={hasOverdue} onChange={(e) => { setHasOverdue(e.target.checked); setPage(1); }} />
          Has overdue
        </label>
        <label className="jobs-list__toggle">
          <input type="checkbox" checked={hasOpenTasks} onChange={(e) => { setHasOpenTasks(e.target.checked); setPage(1); }} />
          Has open tasks
        </label>
        <label className="jobs-list__toggle">
          <input type="checkbox" checked={hasUnpaid} onChange={(e) => { setHasUnpaid(e.target.checked); setPage(1); }} />
          Unpaid invoices
        </label>
        <label className="jobs-list__toggle">
          <input type="checkbox" checked={notFullyInvoiced} onChange={(e) => { setNotFullyInvoiced(e.target.checked); setPage(1); }} />
          Not fully invoiced
        </label>
        {hasActiveFilters ? (
          <Button variant="ghost" size="sm" className="jobs-list__clear" onClick={clearAllFilters}>
            Clear filters
          </Button>
        ) : null}
      </div>

      <div className="jobs-list__stages">
        {visibleStatuses.map((s) => (
          <button
            key={s}
            type="button"
            className={`jobs-stage-chip${stages.has(s) ? " jobs-stage-chip--on" : ""}`}
            aria-pressed={stages.has(s)}
            onClick={() => toggleStage(s)}
          >
            {jobStatusLabel(s)}
          </button>
        ))}
        {stages.size > 0 ? (
          <button type="button" className="jobs-stage-chip jobs-stage-chip--clear" onClick={() => { setStages(new Set()); setPage(1); }}>
            Clear
          </button>
        ) : null}
      </div>

      {visibleError ? <p className="leads__error" role="alert" onClick={() => setDismissedError(error)}>{visibleError}</p> : null}

      {loading ? (
        <div className="jobs-list__loading"><Spinner label="Loading jobs…" /></div>
      ) : items.length === 0 ? (
        <p className="jobs-list__empty">No jobs match these filters.</p>
      ) : (
        <div className="jobs-table" role="table" aria-label="Jobs">
          <div className="jobs-table__head" role="row">
            <SortHeader label="Job #" field="job_number" sortField={sortField} dir={sortDirection} onSort={applySort} />
            <SortHeader label="Job name" field="job_name" sortField={sortField} dir={sortDirection} onSort={applySort} />
            <SortHeader label="Client" field="client_display" sortField={sortField} dir={sortDirection} onSort={applySort} />
            <SortHeader label="Overall status" field="stage" sortField={sortField} dir={sortDirection} onSort={applySort} />
            <span role="columnheader">Planning</span>
            <span role="columnheader">Building Regs</span>
            <span role="columnheader">Drawings</span>
            <SortHeader label="Invoiced" field="total_invoiced" sortField={sortField} dir={sortDirection} onSort={applySort} />
            <SortHeader label="Last active" field="last_activity" sortField={sortField} dir={sortDirection} onSort={applySort} />
          </div>
          {items.map((row) => (
            <Link key={row.id} to={`/staff/jobs/${row.id}`} className="jobs-table__row" role="row">
              <span role="cell" data-label="Job #" className="jobs-table__ref numeric">
                {row.has_overdue ? <span className="jobs-table__overdue-dot" title="Has an overdue deadline" aria-label="Has an overdue deadline" /> : null}
                {row.job_number}
              </span>
              <span role="cell" data-label="Job name">{row.job_name || "—"}</span>
              <span role="cell" data-label="Client">{row.client_display || "—"}</span>
              {/* Overall status = the job's derived stage (Addendum 2). Replaces
                  the old Address column and owns the `stage` sort. */}
              <span role="cell" data-label="Overall status" className="jobs-table__overall">
                <JobStagePill stage={row.stage} />
              </span>
              {/* Per-type drawing-workstream rollups, one column each (Planning /
                  BC / Drawings); "—" when that type has no drawings. Distinct from
                  the job-level overall status above. */}
              <span role="cell" data-label="Planning" className="jobs-table__wscol">
                <WorkstreamStatusCell status={row.planning_status} />
              </span>
              <span role="cell" data-label="Building Regs" className="jobs-table__wscol">
                <WorkstreamStatusCell status={row.bc_status} />
              </span>
              <span role="cell" data-label="Drawings" className="jobs-table__wscol">
                <WorkstreamStatusCell status={row.other_status} />
              </span>
              {/* Billing rollup: the invoiced-to-date amount in a single pill whose
                  colour carries both completeness AND payment state (red = overdue,
                  amber = outstanding, else green/stone/muted by completeness) — the
                  separate Unpaid/Overdue chips were folded in to cut clutter. All
                  derives from server-side fee/invoice sums; jobs with no billable fee
                  value (has_billing=false) show a neutral "—". See billingPill(). */}
              <span role="cell" data-label="Invoiced" className="jobs-table__billing">
                {row.has_billing ? (
                  <span
                    className={`jobs-table__invoiced-pill jobs-table__invoiced-pill--${billingPill(row).tone} numeric`}
                    title={billingPill(row).title}
                  >
                    {formatMoney(row.total_invoiced)}
                  </span>
                ) : (
                  <span className="jobs-table__na">—</span>
                )}
              </span>
              {/* Server recency stamp (Unix ms). null on activity-less shells →
                  formatDateShort renders "—". Owns the default `last_activity` sort. */}
              <span role="cell" data-label="Last active" className="jobs-table__activity">
                {formatDateShort(row.last_activity_at)}
              </span>
            </Link>
          ))}
        </div>
      )}

      {!loading && totalPages > 1 ? (
        <div className="jobs-pager">
          <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
            ← Previous
          </Button>
          <span className="jobs-pager__status">Page {page} of {totalPages}</span>
          <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
            Next →
          </Button>
        </div>
      ) : null}

      {showNew ? <NewJobDrawer onClose={() => setShowNew(false)} /> : null}
    </section>
  );
}

function SortHeader({
  label,
  field,
  sortField,
  dir,
  onSort,
}: {
  label: string;
  field: JobSortField;
  sortField: JobSortField;
  dir: "asc" | "desc";
  onSort: (field: JobSortField) => void;
}) {
  const active = sortField === field;
  return (
    <button
      type="button"
      role="columnheader"
      className={`jobs-table__sort${active ? " jobs-table__sort--active" : ""}`}
      aria-sort={active ? (dir === "asc" ? "ascending" : "descending") : "none"}
      onClick={() => onSort(field)}
    >
      {label}
      <span className="jobs-table__sort-arrow" aria-hidden="true">{active ? (dir === "asc" ? "↑" : "↓") : "↕"}</span>
    </button>
  );
}
